import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData, postData, updateData } from '../../../apiService';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import './Item.css';

function ItemIndex() {
    const [data, setData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [name, setName] = useState('');
    const [gstRate, setGstRate] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editItemId, setEditItemId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editGstRate, setEditGstRate] = useState('');
    const [currentEditItem, setCurrentEditItem] = useState(null);
    const [editErrorMessage, setEditErrorMessage] = useState('');
    const [editSuccessMessage, setEditSuccessMessage] = useState('');

    const history = useHistory();
    const { isAuthenticated, companyId, permissions, token, logout, userId } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        fetchtableData();
    }, [isAuthenticated, companyId, token]);

    const fetchtableData = () => {
        fetchData('CategoryApi', '/' + companyId, token)
            .then((r) => { if (r.responseCode === '1111') setData(r.response_data); })
            .catch((e) => { console.error('Error fetching Item Details:', e); });
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((v) => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleDelete = () => {
        deleteData('CategoryApi', '' + deleteItemId, token)
            .then(() => { fetchtableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
            .catch((e) => console.error('Error deleting record:', e));
    };

    const handleActivate = (id) => {
        activateData('CategoryApi', '' + id, token)
            .then(() => fetchtableData())
            .catch((e) => console.error('Error activating record:', e));
    };

    const openCreateDialog = () => {
        setName(''); setGstRate(''); setErrorMessage(''); setSuccessMessage('');
        setCreateDialogOpen(true);
    };

    const closeCreateDialog = () => {
        setName(''); setGstRate(''); setErrorMessage(''); setSuccessMessage('');
        setCreateDialogOpen(false);
    };

    const submitCreateData = (e) => {
        e.preventDefault();
        if (!name) { setErrorMessage('Name is required!'); return; }
        postData('CategoryApi', { Name: name, GSTRate: gstRate, CreatedBy: userId, CompanyId: companyId, CreatedDate: new Date() }, token)
            .then(() => {
                setSuccessMessage('Item created successfully!');
                setErrorMessage('');
                fetchtableData();
                setTimeout(() => { setSuccessMessage(''); closeCreateDialog(); }, 1500);
            })
            .catch((e) => { console.error('Item Create error:', e); setErrorMessage('Error creating item.'); });
    };

    const openEditDialog = (item) => {
        setCurrentEditItem(item); setEditItemId(item.id);
        setEditName(item.name); setEditGstRate(item.gstRate || '');
        setEditErrorMessage(''); setEditSuccessMessage('');
        setEditDialogOpen(true);
    };

    const closeEditDialog = () => {
        setEditDialogOpen(false); setEditItemId(null); setCurrentEditItem(null);
        setEditName(''); setEditGstRate(''); setEditErrorMessage(''); setEditSuccessMessage('');
    };

    const submitEditData = (e) => {
        e.preventDefault();
        if (!editName) { setEditErrorMessage('Name is required!'); return; }
        const payload = { id: editItemId, name: editName, gstRate: editGstRate, UpdatedBy: userId, CompanyId: currentEditItem?.companyId || companyId, IsActive: currentEditItem?.isActive !== undefined ? currentEditItem.isActive : true, CreatedBy: currentEditItem?.createdBy || userId, CreatedDate: currentEditItem?.createdDate || new Date() };
        updateData('CategoryApi', '' + editItemId, payload, token)
            .then(() => {
                setEditSuccessMessage('Item updated successfully!');
                fetchtableData();
                setTimeout(() => { setEditSuccessMessage(''); closeEditDialog(); }, 1500);
            })
            .catch((e) => { console.error('Item Update error:', e); setEditErrorMessage('Error updating item.'); });
    };

    return (
        <div className="item-container">
            <div className="item-header-card">
                <div className="item-header-content">
                    <div className="item-title-section">
                        <span className="item-title-icon"><CategoryIcon fontSize="small" /></span>
                        <h1 className="item-title">Item</h1>
                    </div>
                    <div className="item-header-actions">
                        <div className={'item-search-wrapper' + (searchOpen ? ' open' : '')}>
                            <span className="item-search-icon" onClick={() => setSearchOpen(function(o) { return !o; })}>
                                <SearchIcon fontSize="small" />
                            </span>
                            {searchOpen && (
                                <input autoFocus className="item-search-input" type="text" placeholder="Search..."
                                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }} />
                            )}
                        </div>
                        {hasCreateMasterPermission && (
                            <button className="it-btn it-btn-create" onClick={openCreateDialog}>
                                <AddIcon style={{ fontSize: 14 }} />&nbsp;New Item
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="item-table-wrapper">
                <TableContainer component={Paper} className="item-table-container" elevation={0}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className="item-th">Sl.No</TableCell>
                                <TableCell className="item-th">Name</TableCell>
                                <TableCell className="item-th" align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((item, index) => (
                                <TableRow key={item.id} className={'item-row' + (!item.isActive ? ' item-row-inactive' : '')}>
                                    <TableCell className="item-td">{index + 1}</TableCell>
                                    <TableCell className="item-td">{item.name}</TableCell>
                                    <TableCell className="item-td" align="center">
                                        <div className="item-action-btns">
                                            <Link to={'/itemDetail/' + item.id}>
                                                <IconButton className="item-icon-btn item-icon-detail" size="small" title="Detail">
                                                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Link>
                                            {hasEditMasterPermission && (
                                                <IconButton className="item-icon-btn item-icon-edit" size="small" title="Edit" onClick={() => openEditDialog(item)}>
                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            )}
                                            {hasDeleteMasterPermission && (
                                                <IconButton
                                                    className={'item-icon-btn ' + (item.isActive ? 'item-icon-delete' : 'item-icon-activate')}
                                                    size="small"
                                                    title={item.isActive ? 'Delete' : 'Activate'}
                                                    onClick={() => item.isActive ? (setDeleteItemId(item.id), setConfirmDialogOpen(true)) : handleActivate(item.id)}
                                                >
                                                    {item.isActive ? <DeleteIcon sx={{ fontSize: 16 }} /> : <ActivateIcon sx={{ fontSize: 16 }} />}
                                                </IconButton>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} PaperProps={{ style: { borderRadius: '10px', padding: '4px' } }}>
                <DialogTitle style={{ fontSize: '16px', fontWeight: 600 }}>Delete Record?</DialogTitle>
                <DialogContent><DialogContentText style={{ fontSize: '13px' }}>Are you sure you want to delete this record?</DialogContentText></DialogContent>
                <DialogActions style={{ padding: '12px 20px', gap: '8px' }}>
                    <button className="item-dialog-btn it-btn it-btn-cancel" onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
                    <button className="item-dialog-btn it-btn it-btn-back" onClick={handleDelete}>Delete</button>
                </DialogActions>
            </Dialog>

            <Dialog open={createDialogOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: '10px' } }}>
                <DialogTitle style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: '#fff', fontSize: '14px', fontWeight: 700 }}>Create New Item</DialogTitle>
                <DialogContent style={{ padding: '20px' }}>
                    {errorMessage && <div className="item-alert item-alert-error">{errorMessage}</div>}
                    {successMessage && <div className="item-alert item-alert-success">{successMessage}</div>}
                    <div className="item-form-row" style={{ marginTop: '8px' }}>
                        <div className="item-form-group w-3">
                            <label className="item-label">Item Name <span className="required">*</span></label>
                            <input className="item-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter item name" maxLength={50} />
                        </div>
                        <div className="item-form-group">
                            <label className="item-label">GST Rate</label>
                            <input className="item-input text-right" type="number" value={gstRate} onChange={(e) => setGstRate(e.target.value)} placeholder="0" />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '12px 20px', gap: '8px' }}>
                    <button className="it-btn it-btn-cancel" onClick={closeCreateDialog}>Cancel</button>
                    <button className="it-btn it-btn-save" onClick={submitCreateData}>Save</button>
                </DialogActions>
            </Dialog>

            <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: '10px' } }}>
                <DialogTitle style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: '#fff', fontSize: '14px', fontWeight: 700 }}>Edit Item</DialogTitle>
                <DialogContent style={{ padding: '20px' }}>
                    {editErrorMessage && <div className="item-alert item-alert-error">{editErrorMessage}</div>}
                    {editSuccessMessage && <div className="item-alert item-alert-success">{editSuccessMessage}</div>}
                    <div className="item-form-row" style={{ marginTop: '8px' }}>
                        <div className="item-form-group w-3">
                            <label className="item-label">Item Name <span className="required">*</span></label>
                            <input className="item-input" type="text" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Enter item name" maxLength={50} />
                        </div>
                        <div className="item-form-group">
                            <label className="item-label">GST Rate</label>
                            <input className="item-input text-right" type="number" value={editGstRate} onChange={(e) => setEditGstRate(e.target.value)} placeholder="0" />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '12px 20px', gap: '8px' }}>
                    <button className="it-btn it-btn-cancel" onClick={closeEditDialog}>Cancel</button>
                    <button className="it-btn it-btn-update" onClick={submitEditData}>Update</button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ItemIndex;
